import { c as _c } from "react/compiler-runtime";
import { skipToken, useQuery, useTRPC } from '@/api/trpc';
import { useMemo } from 'react';
import { GetIntegrationListParams } from '@repo/common/schemas/api';
import * as R from 'remeda';
export function useAccount(accountId) {
  const $ = _c(7);
  const trpc = useTRPC();
  let t0;
  if ($[0] !== accountId || $[1] !== trpc.accounts.byId) {
    t0 = trpc.accounts.byId.queryOptions(accountId ? {
      accountId
    } : skipToken);
    $[0] = accountId;
    $[1] = trpc.accounts.byId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    data: account,
    isLoading,
    error
  } = useQuery(t0);
  let t1;
  if ($[3] !== account || $[4] !== error || $[5] !== isLoading) {
    t1 = {
      account,
      isLoading,
      error
    };
    $[3] = account;
    $[4] = error;
    $[5] = isLoading;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

/**
 * Hook to fetch integrations for an account using React Query
 */
export function useIntegrations(accountId, params, options) {
  const $ = _c(15);
  const trpc = useTRPC();
  let t0;
  if ($[0] !== accountId || $[1] !== params || $[2] !== trpc.integrations.list) {
    t0 = trpc.integrations.list.queryOptions({
      account: accountId ?? undefined,
      params
    });
    $[0] = accountId;
    $[1] = params;
    $[2] = trpc.integrations.list;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = options?.refetchInterval;
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      ...t0,
      refetchInterval: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    data: integrations,
    isLoading,
    isFetching,
    error,
    dataUpdatedAt
  } = useQuery(t2);
  let t3;
  if ($[7] !== integrations) {
    t3 = integrations ?? [];
    $[7] = integrations;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== dataUpdatedAt || $[10] !== error || $[11] !== isFetching || $[12] !== isLoading || $[13] !== t3) {
    t4 = {
      integrations: t3,
      isLoading,
      isFetching,
      error,
      dataUpdatedAt
    };
    $[9] = dataUpdatedAt;
    $[10] = error;
    $[11] = isFetching;
    $[12] = isLoading;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export function usePrimaryIntegration(accountId, options) {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "bullhorn",
      status: "active"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = useIntegrations(accountId, t0, options);
  let integrations;
  let rest;
  if ($[1] !== t1) {
    ({
      integrations,
      ...rest
    } = t1);
    $[1] = t1;
    $[2] = integrations;
    $[3] = rest;
  } else {
    integrations = $[2];
    rest = $[3];
  }
  let t2;
  if ($[4] !== integrations) {
    t2 = R.only(integrations) ?? null;
    $[4] = integrations;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== rest || $[7] !== t2) {
    t3 = {
      integration: t2,
      ...rest
    };
    $[6] = rest;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// Hook to get an account + integration by corpId, filtered by the authenticated user's permissions
// If the user can only access 1 integration and didn't provide a corpId, use that one
export function useAccountByCorpId(corpId) {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "bullhorn",
      status: "active"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    integrations: t1,
    isLoading,
    error
  } = useIntegrations(null, t0);
  let t2;
  if ($[1] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const allIntegrations = t2;
  let t3;
  if ($[3] !== allIntegrations || $[4] !== corpId) {
    t3 = corpId ? allIntegrations.find(integration => integration.corpId === corpId) : R.only(allIntegrations);
    $[3] = allIntegrations;
    $[4] = corpId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const integration_0 = t3;
  const account = integration_0?.account;
  let t4;
  if ($[6] !== account || $[7] !== error || $[8] !== integration_0 || $[9] !== isLoading) {
    t4 = {
      account,
      integration: integration_0,
      isLoading,
      error
    };
    $[6] = account;
    $[7] = error;
    $[8] = integration_0;
    $[9] = isLoading;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}